import React, { useState } from "react"
import { graphql, useStaticQuery } from "gatsby"
import IconButton from "@mui/material/IconButton"
import { LiaBarsSolid, LiaTimesSolid } from "react-icons/lia"
import { useMediaQuery, useTheme, Drawer } from "@mui/material"
import { styled } from "@mui/material/styles"
import MainNavigation from "./mainNavigation"
import NavigationLogo from "./navigationLogo"
import { ButtonContactUs } from "./buttonContactUs"
import { contrastBrandPalette } from "../utils/colours"
import { useMenuContext } from "../components/utils/useMenuContext"

const Wrapper = styled("div")({
  display: "flex",
  justifyContent: "center",
  position: "relative",
  zIndex: 10,
})

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "95%",
  margin: "1rem 0 0",
  padding: "var(--ms-1)",
  backgroundColor: contrastBrandPalette["Original Large"]?.value,
  borderRadius: "99px",
  border: "3px solid rgba(255,255,255,0.6)",
  position: "fixed",
  [theme.breakpoints.up("lg")]: {
    justifyContent: "flex-start",
    padding: "var(--ms0)",
  },
}))

const DesktopNav = styled("div")(({ theme }) => ({
  display: "none",
  flex: 1,
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}))

const MobileDrawerBox = styled("div")({
  width: "100vw",
  height: "100%",
  minHeight: "100vh",
  padding: "2rem",
  backgroundColor: "#2661ab",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  position: "relative", // ensures inner scrolling works
})

const MenuButton = styled("div")({
  pointerEvents: "visible",
})

const NavigationWrapper = styled("div")(
  ({ navOpen, showMenuButton, theme }) => ({
    position: "relative",
    visibility: showMenuButton ? (navOpen ? "visible" : "hidden") : "visible",
    opacity: showMenuButton ? (navOpen ? 1 : 0) : 1,
    pointerEvents: showMenuButton ? (navOpen ? "auto" : "none") : "auto",
    transition: "opacity 0.3s ease, visibility 0.3s ease",
    [theme.breakpoints.up("lg")]: {
      height: "fit-content",
      margin: "0 auto",
      overflowY: "unset",
    },
  }),
)

const Header = () => {
  const { navOpen, toggleOpenNavMenu, setActiveMenu } = useMenuContext()
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down("sm"))
  const tablet = useMediaQuery(theme.breakpoints.between("sm", "md"))
  const showMenuButton = mobile || tablet

  const [drawerOpen, setDrawerOpen] = useState(false)

  const handleFullCloseNav = () => {
    setActiveMenu(null)
    toggleOpenNavMenu(false)
    setDrawerOpen(false)
  }

  const handleMenuToggle = () => {
    setActiveMenu(null)
    toggleOpenNavMenu(!navOpen)
    setDrawerOpen(!drawerOpen)
  }

  const handleDrawerClose = () => {
    setActiveMenu(null)
    toggleOpenNavMenu(false)
    setDrawerOpen(false)
  }

  const data = useStaticQuery(graphql`
    query MainNavigationQuery {
      sanityNavigation(navId: { current: { eq: "main-menu" } }) {
        items {
          _type
          _key
          link {
            link {
              external
              internal {
                ... on SanityPage {
                  id
                  slug {
                    current
                  }
                }
                ... on SanityPost {
                  id
                  slug {
                    current
                    _type
                  }
                  categories {
                    name
                    slug {
                      current
                    }
                  }
                }
              }
            }
            text
          }
          childItems {
            _key
            text
            link {
              external
              internal {
                ... on SanityPage {
                  id
                  slug {
                    current
                  }
                }
                ... on SanityPost {
                  id
                  slug {
                    current
                    _type
                  }
                  categories {
                    name
                    slug {
                      current
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `)

  return (
    <Wrapper>
      <Container>
        <NavigationLogo />
        {/* Desktop Navigation */}
        <DesktopNav>
          <NavigationWrapper navOpen={navOpen} showMenuButton={showMenuButton}>
            <MainNavigation
              data={data?.sanityNavigation?.items}
              isMobile={false}
              handleCloseNavMenu={handleFullCloseNav}
            />
          </NavigationWrapper>
        </DesktopNav>
        <div className={`button-contact-us`}>
          <ButtonContactUs />
        </div>
        {showMenuButton && (
          <MenuButton>
            <IconButton
              size="large"
              onClick={handleMenuToggle}
              color="white"
              aria-label="toggle menu"
              disableRipple
            >
              <LiaBarsSolid />
            </IconButton>
          </MenuButton>
        )}
      </Container>

      {/* Mobile Drawer */}
      {showMenuButton && (
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
        >
          <MobileDrawerBox>
            <NavigationLogo style={{ marginBottom: "2rem" }} />
            <IconButton
              size="large"
              aria-label="close menu"
              onClick={handleDrawerClose}
              sx={{
                position: "absolute",
                top: "1.88rem",
                right: "1rem",
                color: "var(--white)",
              }}
            >
              <LiaTimesSolid />
            </IconButton>
            <MainNavigation
              data={data?.sanityNavigation?.items}
              isMobile={true}
              handleCloseNavMenu={handleFullCloseNav}
            />
            <ButtonContactUs
              sx={{
                marginTop: "2rem",
                width: "100%",
                justifyContent: "center",
              }}
            />
          </MobileDrawerBox>
        </Drawer>
      )}
    </Wrapper>
  )
}

export default Header
